import { FormEvent } from 'react'

import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { APPEAL_TYPES } from 'entities/appeal/model'

import { AppealsSubjects, KeyValue } from 'shared/api/AppealModel'
import { MyTicketItem } from 'shared/api/MyTicketsModel.ts'
import { EMAIL_REGEX, REGEX_EMAIL } from 'shared/constants/regex'
import { validatePhone } from 'shared/lib/validatePhone'
import { ButtonContinue } from 'shared/ui/bottom-continue'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { Input } from 'shared/ui/input'
import { Paper } from 'shared/ui/paper'
import { IPhoneInput, PhoneInput } from 'shared/ui/phone-input'
import { PopupContent } from 'shared/ui/poup-content'
import { Option } from 'shared/ui/select/select'

import { AppealForm } from './components/appeal-form'
import styles from './styles.module.scss'

export interface FormFeedBack {
  phone: IPhoneInput
  email?: string
  files?: File[]
  subject?: string
  topic?: string
  description?: string
  typeFeedback?: string
  passengers?: Option[]
}

interface Props {
  topics?: AppealsSubjects
  onSubmit?: (data: FormFeedBack) => void
  ticket?: MyTicketItem
}

const PopUpText = {
  [APPEAL_TYPES.REFUND_TICKET_SUBJECTS]: 'feed_back.refund_description_before',
  [APPEAL_TYPES.TRADE_TICKET_SUBJECTS]: 'feed_back.exchange_description_before',
  [APPEAL_TYPES.OTHER_SUBJECTS]: 'feed_back.common_description_before',
}

export const ApplyFormTicket = ({ topics, onSubmit, ticket }: Props) => {
  const { t } = useTranslation()

  const { openDialog, closeDialog } = useDialogBottomSheet()

  const methods = useForm<FormFeedBack>()

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = methods

  const onSubmitHandler = handleSubmit((data) => {
    openDialog({
      isDialog: false,
      child: (
        <PopupContent
          button={t('continue')}
          title={t('sent_appeal')}
          description={t(PopUpText[data.topic as keyof object])}
          onClick={() => {
            closeDialog()
            onSubmit?.(data)
          }}
        />
      ),
    })
  })

  const onChangeInputEmail = (event: FormEvent<HTMLInputElement>) => {
    return event?.currentTarget?.value?.replace(REGEX_EMAIL, '')
  }

  const passengersOptions =
    ticket?.flight_details?.tickets?.[0]?.passengers?.map((passenger) => ({
      value: `${passenger?.key || ''}`,
      name: `${passenger?.name?.last || ''} ${passenger?.name?.first} ${
        passenger?.name?.middle || ''
      }`,
    }))

  const listTypeOfAppeal: { value: string; name: string }[] & Array<[]> = []

  if (topics?.other_subjects?.length) {
    listTypeOfAppeal.push({
      value: APPEAL_TYPES.OTHER_SUBJECTS,
      name: t('feed_back.edit_info'),
    })
  }

  if (topics?.refund_ticket_subjects?.length) {
    listTypeOfAppeal.push({
      value: APPEAL_TYPES.REFUND_TICKET_SUBJECTS,
      name: t('feed_back.refund_ticket'),
    })
  }

  if (topics?.trade_ticket_subjects?.length) {
    listTypeOfAppeal.push({
      value: APPEAL_TYPES.TRADE_TICKET_SUBJECTS,
      name: t('feed_back.exchange_ticket'),
    })
  }

  const selectedTypeAppeal = useWatch({ control, name: 'topic' })

  const listTopics: KeyValue[] | [] =
    topics?.[selectedTypeAppeal as keyof object] || []

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmitHandler}>
        <div className={styles.contactWrapper}>
          <Paper padding={[20]}>
            <p className={styles.title}>{t('purchase_page.contact_title')}</p>

            <div className={styles.contactFieldForm}>
              <Controller
                name="phone"
                control={control}
                rules={{
                  validate: validatePhone,
                }}
                render={({ field }) => (
                  <PhoneInput
                    error={errors.phone}
                    value={field?.value}
                    onChange={(value) => field.onChange(value)}
                  />
                )}
              />

              <Controller
                name="email"
                control={control}
                rules={{
                  pattern: {
                    value: EMAIL_REGEX,
                    message: '',
                  },
                }}
                render={({ field }) => (
                  <Input
                    lang={'en'}
                    value={field.value}
                    error={errors?.email}
                    placeholder={t('email')}
                    onChange={(event) =>
                      field.onChange(onChangeInputEmail(event))
                    }
                  />
                )}
              />
            </div>
          </Paper>
        </div>

        <AppealForm
          topics={topics}
          listTopics={listTopics}
          listTypeOfAppeal={listTypeOfAppeal}
          passengersOptions={passengersOptions}
        />

        <ButtonContinue>
          <span className={styles.text}>{t('sent')}</span>
        </ButtonContinue>
      </form>
    </FormProvider>
  )
}
